import type { ServerSentEvent } from "./streamTypes";

export function parseSseChunk(chunk: string): ServerSentEvent | null {
  const lines = chunk
    .split("\n")
    .map((line) => line.replace(/\r/g, ""))
    .filter((line) => line.trim().length > 0);

  const event: ServerSentEvent = { data: "" };

  for (const line of lines) {
    if (line.startsWith(":")) {
      continue;
    }

    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) {
      continue;
    }

    const field = line.slice(0, separatorIndex).trim();
    const value = line.slice(separatorIndex + 1).trimStart();

    switch (field) {
      case "id":
        event.id = value;
        break;
      case "event":
        event.event = value;
        break;
      case "data":
        event.data = event.data ? `${event.data}\n${value}` : value;
        break;
      default:
        break;
    }
  }

  if (event.data === "") {
    return null;
  }

  return event;
}

export async function readEventStream(response: Response, onEvent: (event: ServerSentEvent) => void): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Missing response body for event stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    let boundaryIndex = buffer.indexOf("\n\n");

    while (boundaryIndex !== -1) {
      const rawEvent = buffer.slice(0, boundaryIndex);
      buffer = buffer.slice(boundaryIndex + 2);

      const parsed = parseSseChunk(rawEvent);
      if (parsed) {
        onEvent(parsed);
      }

      boundaryIndex = buffer.indexOf("\n\n");
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const parsed = parseSseChunk(buffer);
    if (parsed) {
      onEvent(parsed);
    }
  }
}

export async function* streamEvents(response: Response): AsyncGenerator<ServerSentEvent, void, unknown> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Missing response body for event stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    let boundaryIndex = buffer.indexOf("\n\n");

    while (boundaryIndex !== -1) {
      const rawEvent = buffer.slice(0, boundaryIndex);
      buffer = buffer.slice(boundaryIndex + 2);

      const parsed = parseSseChunk(rawEvent);
      if (parsed) {
        yield parsed;
      }

      boundaryIndex = buffer.indexOf("\n\n");
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const parsed = parseSseChunk(buffer);
    if (parsed) {
      yield parsed;
    }
  }
}
